import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { MagnifyingGlass, Plus, PencilSimple, Trash, UserPlus, Buildings, MapPin, User, Eye, EyeSlash, Tag, Stamp, X, GitMerge, ClockCounterClockwise, Warning } from '@phosphor-icons/react';
import { ChevronDown, ChevronRight, Building2 } from 'lucide-react';
import { TrashDialog, MergeDialog, HistoryDialog } from './EstablishmentTools';

export default function ClientAccountManager({ onCompanyMarquage } = {}) {
  const [subTab, setSubTab] = useState('comptes');

  return (
    <div data-testid="client-account-manager">
      {/* Sub-tab navigation */}
      <div className="flex gap-1 mb-6 border-b border-zinc-200 pb-0">
        {[
          { key: 'comptes', label: 'Comptes clients' },
          { key: 'societes', label: 'Sociétés' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${subTab === t.key ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-600'}`}
            data-testid={`subtab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'comptes' && <ComptesTab onCompanyMarquage={onCompanyMarquage} />}
      {subTab === 'societes' && <SocietesTab />}
    </div>
  );
}

// ============================================================
// SUB-TAB: Comptes clients (établissements + utilisateurs)
// ============================================================
function ComptesTab({ onCompanyMarquage } = {}) {
  const [companies, setCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [companyFull, setCompanyFull] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [createMode, setCreateMode] = useState(false);

  // Create: establishment + users linked to existing company
  const [createCompanyId, setCreateCompanyId] = useState('');
  const [formEstablishment, setFormEstablishment] = useState({ name: '', ville: '', address: '', email: '', phone: '' });
  const [formNewUsers, setFormNewUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  // Edit form state
  const [formCompany, setFormCompany] = useState({ name: '', siret: '', enseigne: '', email: '', phone: '', address: '' });
  const [formEstablishments, setFormEstablishments] = useState([]);
  const [editNewUsers, setEditNewUsers] = useState({});

  // Add user dialog
  const [addUserDialog, setAddUserDialog] = useState({ open: false, estId: '' });
  const [newUserForm, setNewUserForm] = useState({ name: '', email: '', phone: '', password: '', existing_user: null });

  // Corbeille / Fusion / Historique
  const [trashOpen, setTrashOpen] = useState(false);
  const [mergeState, setMergeState] = useState({ open: false, source: null });
  const [historyOpen, setHistoryOpen] = useState(false);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const url = searchQuery.trim()
        ? `${API}/companies/search-all?q=${encodeURIComponent(searchQuery)}`
        : `${API}/companies`;
      const r = await axios.get(url);
      setCompanies(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [searchQuery]);

  useEffect(() => {
    const t = setTimeout(fetchCompanies, 300);
    return () => clearTimeout(t);
  }, [fetchCompanies]);

  const loadCompanyFull = async (companyId) => {
    try {
      const r = await axios.get(`${API}/companies/${companyId}/full`);
      setCompanyFull(r.data);
      setSelectedCompanyId(companyId);
    } catch { toast.error('Erreur chargement'); }
  };

  // ---- Create mode: new establishment linked to existing company ----
  const startCreate = () => {
    setCreateMode(true);
    setEditMode(false);
    setSelectedCompanyId(null);
    setCompanyFull(null);
    setCreateCompanyId('');
    setFormEstablishment({ name: '', ville: '', address: '', email: '', phone: '' });
    setFormNewUsers([]);
  };

  const addPendingUser = () => {
    setFormNewUsers(prev => [...prev, { _id: Date.now(), name: '', email: '', phone: '', password: '', existing_user: null, link_existing: false }]);
  };

  // Look up email — invoked on blur of an email field for any pending user
  const lookupEmail = async (email) => {
    const e = (email || '').trim();
    if (!e) return null;
    try {
      const r = await axios.get(`${API}/clients/lookup`, { params: { email: e } });
      return r.data?.exists ? r.data.user : null;
    } catch {
      return null;
    }
  };

  const onPendingUserEmailBlur = async (estKey, idx, email, isEditMode) => {
    const found = await lookupEmail(email);
    if (!found) return;
    if (isEditMode) {
      setEditNewUsers(prev => {
        const arr = [...(prev[estKey] || [])];
        if (!arr[idx]) return prev;
        arr[idx] = { ...arr[idx], existing_user: found, link_existing: true, name: found.name || arr[idx].name };
        return { ...prev, [estKey]: arr };
      });
    } else {
      setFormNewUsers(prev => prev.map((u, i) => i === idx
        ? { ...u, existing_user: found, link_existing: true, name: found.name || u.name }
        : u));
    }
    toast.info(`Utilisateur existant détecté : ${found.name} (${found.company_name || 'sans société'})`);
  };

  const setPendingUserLinkMode = (estKey, idx, link, isEditMode) => {
    if (isEditMode) {
      setEditNewUsers(prev => {
        const arr = [...(prev[estKey] || [])];
        if (!arr[idx]) return prev;
        arr[idx] = { ...arr[idx], link_existing: link };
        return { ...prev, [estKey]: arr };
      });
    } else {
      setFormNewUsers(prev => prev.map((u, i) => i === idx ? { ...u, link_existing: link } : u));
    }
  };

  const handleCreateSave = async () => {
    if (!createCompanyId) { toast.error("Veuillez sélectionner une société"); return; }
    if (!formEstablishment.name.trim()) { toast.error("Le nom de l'établissement est obligatoire"); return; }
    if (!formEstablishment.email.trim()) { toast.error("L'email de l'établissement est obligatoire"); return; }
    setSaving(true);
    try {
      const company = companies.find(c => c.id === createCompanyId);
      const estRes = await axios.post(`${API}/establishments`, {
        company_id: createCompanyId,
        name: formEstablishment.name.trim(),
        ville: formEstablishment.ville.trim(),
        address: formEstablishment.address.trim(),
        email: formEstablishment.email.trim(),
        phone: formEstablishment.phone.trim(),
      });
      const estId = estRes.data.id;
      // Create or link users to this establishment
      for (const pu of formNewUsers) {
        if (pu.link_existing && pu.existing_user?.id) {
          // Link existing user
          try {
            await axios.post(`${API}/establishments/${estId}/attach-user`, {
              user_id: pu.existing_user.id,
            });
          } catch (err) {
            toast.error(`Lien échoué pour ${pu.existing_user.email} : ${err.response?.data?.detail || 'Erreur'}`);
          }
          continue;
        }
        if (!pu.name.trim() || !pu.email.trim()) continue;
        await axios.post(`${API}/clients`, {
          name: pu.name.trim(), email: pu.email.trim(), phone: pu.phone || '',
          password: pu.password || undefined,
          company_id: createCompanyId, company_name: company?.name || '',
          establishment_ids: [estId],
        });
      }
      toast.success(`Établissement "${formEstablishment.name}" créé`);
      setCreateMode(false);
      await fetchCompanies();
      await loadCompanyFull(createCompanyId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally { setSaving(false); }
  };

  // ---- Edit mode (company + establishments) ----
  const startEdit = () => {
    if (!companyFull) return;
    setEditMode(true);
    setCreateMode(false);
    setFormCompany({
      name: companyFull.name || '', siret: companyFull.siret || '',
      enseigne: companyFull.enseigne || '', email: companyFull.email || '',
      phone: companyFull.phone || '', address: companyFull.address || '',
    });
    setFormEstablishments(
      (companyFull.establishments || []).map(e => ({
        id: e.id, name: e.name || '', ville: e.ville || '',
        address: e.address || '', phone: e.phone || '', email: e.email || '',
        users: e.users || [],
      }))
    );
    setEditNewUsers({});
  };

  const cancelEdit = () => { setEditMode(false); setCreateMode(false); };

  const addEstablishment = () => {
    setFormEstablishments(prev => [...prev, { _tempId: Date.now(), name: '', ville: '', address: '', phone: '', email: '' }]);
  };
  const removeEstablishment = async (idx) => {
    const est = formEstablishments[idx];
    if (est?.id) {
      const ok = await deleteEstablishmentApi(est);
      if (!ok) return;
    }
    setFormEstablishments(prev => prev.filter((_, i) => i !== idx));
  };
  const updateEstablishment = (idx, field, value) => {
    setFormEstablishments(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  };

  const handleEditSave = async () => {
    if (!formCompany.name.trim()) { toast.error("Nom obligatoire"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/companies/${selectedCompanyId}`, {
        name: formCompany.name.trim(), siret: formCompany.siret.trim(),
        enseigne: formCompany.enseigne?.trim() || '',
        email: formCompany.email.trim(), phone: formCompany.phone.trim(),
        address: formCompany.address?.trim() || '',
      });
      for (const est of formEstablishments) {
        if (!est.name.trim()) continue;
        if (est.id) {
          await axios.put(`${API}/establishments/${est.id}`, {
            name: est.name.trim(), ville: est.ville?.trim() || '',
            address: est.address.trim(), phone: est.phone.trim(), email: est.email.trim(),
          });
        } else {
          const r = await axios.post(`${API}/establishments`, {
            company_id: selectedCompanyId, name: est.name.trim(),
            ville: est.ville?.trim() || '', address: est.address.trim(),
            phone: est.phone.trim(), email: est.email.trim(),
          });
          const tempKey = est._tempId;
          for (const pu of (editNewUsers[tempKey] || [])) {
            if (pu.link_existing && pu.existing_user?.id) {
              try {
                await axios.post(`${API}/establishments/${r.data.id}/attach-user`, {
                  user_id: pu.existing_user.id,
                });
              } catch (err) {
                toast.error(`Lien échoué pour ${pu.existing_user.email} : ${err.response?.data?.detail || 'Erreur'}`);
              }
              continue;
            }
            if (!pu.name.trim() || !pu.email.trim()) continue;
            await axios.post(`${API}/clients`, {
              name: pu.name.trim(), email: pu.email.trim(), phone: pu.phone || '',
              password: pu.password || undefined,
              company_id: selectedCompanyId, company_name: formCompany.name.trim(),
              establishment_ids: [r.data.id],
            });
          }
        }
      }
      toast.success("Modifications enregistrées");
      setEditMode(false);
      await fetchCompanies();
      await loadCompanyFull(selectedCompanyId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const handleAddUserToEstablishment = async () => {
    const { estId } = addUserDialog;
    if (!newUserForm.email.trim()) { toast.error("Email obligatoire"); return; }

    // If we already detected an existing user, link instead of create
    if (newUserForm.existing_user?.id) {
      try {
        await axios.post(`${API}/establishments/${estId}/attach-user`, {
          user_id: newUserForm.existing_user.id,
        });
        toast.success(`Utilisateur "${newUserForm.existing_user.name}" lié à l'établissement`);
        setAddUserDialog({ open: false, estId: '' });
        setNewUserForm({ name: '', email: '', phone: '', password: '', existing_user: null });
        await loadCompanyFull(selectedCompanyId);
      } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
      return;
    }

    if (!newUserForm.name.trim()) { toast.error("Nom obligatoire"); return; }
    try {
      await axios.post(`${API}/clients`, {
        name: newUserForm.name.trim(), email: newUserForm.email.trim(),
        phone: newUserForm.phone || '', password: newUserForm.password || undefined,
        company_id: selectedCompanyId, company_name: companyFull?.name || '',
        establishment_ids: [estId],
      });
      toast.success(`Utilisateur "${newUserForm.name}" ajouté`);
      setAddUserDialog({ open: false, estId: '' });
      setNewUserForm({ name: '', email: '', phone: '', password: '', existing_user: null });
      await loadCompanyFull(selectedCompanyId);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // Suppression d'un établissement (avec gestion force si commandes rattachées)
  const deleteEstablishmentApi = async (est) => {
    if (!est?.id) return false;
    if (!window.confirm(`Supprimer l'établissement « ${est.name} » ?\nLes utilisateurs rattachés seront détachés (leurs comptes ne sont pas supprimés).`)) return false;
    try {
      await axios.delete(`${API}/establishments/${est.id}`);
      toast.success('Établissement supprimé');
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('commande(s)')) {
        if (window.confirm(`${detail}\n\nForcer la suppression malgré tout ? (les commandes seront conservées)`)) {
          try {
            await axios.delete(`${API}/establishments/${est.id}?force=true`);
            toast.success('Établissement supprimé (commandes conservées)');
            return true;
          } catch { toast.error('Erreur suppression forcée'); }
        }
      } else {
        toast.error(detail || 'Erreur suppression');
      }
      return false;
    }
  };

  const handleDeleteEstablishment = async (est) => {
    const ok = await deleteEstablishmentApi(est);
    if (ok && selectedCompanyId) await loadCompanyFull(selectedCompanyId);
  };

  const handleDialogEmailBlur = async () => {
    const found = await lookupEmail(newUserForm.email);
    if (found) {
      setNewUserForm(p => ({ ...p, existing_user: found, name: p.name || found.name }));
      toast.info(`Utilisateur existant : ${found.name}`);
    } else {
      setNewUserForm(p => ({ ...p, existing_user: null }));
    }
  };

  return (
    <div className="space-y-4">
      <MissingAddressAlert
        refreshKey={companyFull}
        onSelect={(companyId) => { loadCompanyFull(companyId); setEditMode(false); setCreateMode(false); }}
      />
    <div className="grid grid-cols-12 gap-6">
      {/* LEFT: Company list */}
      <div className="col-span-4 space-y-4">
        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={16} />
          <Input placeholder="Rechercher..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 rounded-sm bg-white text-sm" data-testid="client-search" />
        </div>
        <Button onClick={startCreate} className="w-full rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="create-company-btn">
          <Plus size={16} className="mr-2" />Nouveau compte client
        </Button>
        <Button variant="outline" onClick={() => setTrashOpen(true)} className="w-full rounded-sm text-zinc-600" data-testid="open-trash-btn">
          <Trash size={15} className="mr-2" />Corbeille
        </Button>
        <div className="space-y-1 max-h-[calc(100vh-300px)] overflow-y-auto">
          {loading && <p className="text-sm text-zinc-400 text-center py-4">Chargement...</p>}
          {!loading && companies.length === 0 && <p className="text-sm text-zinc-400 text-center py-8">Aucune société trouvée</p>}
          {companies.map(c => (
            <button key={c.id} onClick={() => { loadCompanyFull(c.id); setEditMode(false); setCreateMode(false); }}
              className={`w-full text-left p-3 rounded-sm border transition-all ${selectedCompanyId === c.id && !createMode ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 bg-white hover:border-zinc-400'}`}
              data-testid={`company-card-${c.id}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm text-zinc-900">{c.name}</span>
                <span className="text-[10px] font-mono text-zinc-400">{c.account_number}</span>
              </div>
              {c.siret && <p className="text-xs text-zinc-400 mt-0.5">SIRET: {c.siret}</p>}
            </button>
          ))}
        </div>
      </div>

      {/* RIGHT */}
      <div className="col-span-8">
        {/* CREATE MODE */}
        {createMode && (
          <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-6" data-testid="establishment-create-form">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-semibold text-lg">Nouveau compte client</h2>
              <Button variant="ghost" size="sm" onClick={cancelEdit}><X size={18} /></Button>
            </div>

            {/* Société selection */}
            <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-sm space-y-3">
              <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Société</p>
              <div>
                <Label className="text-xs text-zinc-500">Sélectionner la société *</Label>
                <Select value={createCompanyId} onValueChange={setCreateCompanyId}>
                  <SelectTrigger className="rounded-sm mt-1 bg-white" data-testid="create-company-select"><SelectValue placeholder="Choisir une société" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.siret ? ` (${c.siret})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Establishment info */}
            <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-sm space-y-3">
              <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                <Buildings size={14} className="inline mr-1" />Informations établissement
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-zinc-500">Nom (enseigne) *</Label>
                  <Input value={formEstablishment.name} onChange={e => setFormEstablishment(p => ({ ...p, name: e.target.value }))} className="rounded-sm mt-1 bg-white" data-testid="create-est-name" /></div>
                <div><Label className="text-xs text-zinc-500">Ville</Label>
                  <Input value={formEstablishment.ville} onChange={e => setFormEstablishment(p => ({ ...p, ville: e.target.value }))} className="rounded-sm mt-1 bg-white" data-testid="create-est-ville" /></div>
                <div className="col-span-2"><Label className="text-xs text-zinc-500">Adresse</Label>
                  <Input value={formEstablishment.address} onChange={e => setFormEstablishment(p => ({ ...p, address: e.target.value }))} className="rounded-sm mt-1 bg-white" /></div>
                <div><Label className="text-xs text-zinc-500">Email *</Label>
                  <Input type="email" value={formEstablishment.email} onChange={e => setFormEstablishment(p => ({ ...p, email: e.target.value }))} className="rounded-sm mt-1 bg-white" data-testid="create-est-email" /></div>
                <div><Label className="text-xs text-zinc-500">Téléphone</Label>
                  <Input value={formEstablishment.phone} onChange={e => setFormEstablishment(p => ({ ...p, phone: e.target.value }))} className="rounded-sm mt-1 bg-white" /></div>
              </div>
            </div>

            {/* Users */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wider"><User size={14} className="inline mr-1" />Utilisateurs</p>
                <Button variant="outline" size="sm" onClick={addPendingUser} className="rounded-sm text-xs" data-testid="add-user-btn"><Plus size={12} className="mr-1" />Ajouter</Button>
              </div>
              {formNewUsers.map((pu, idx) => (
                <div key={pu._id} className="border border-zinc-200 rounded-sm p-2 bg-white space-y-2" data-testid={`pending-user-row-${idx}`}>
                  <div className="grid grid-cols-12 gap-1.5 items-center">
                    <Input placeholder="Email *"
                      value={pu.email}
                      onChange={e => setFormNewUsers(prev => prev.map((u, i) => i === idx ? { ...u, email: e.target.value, existing_user: null, link_existing: false } : u))}
                      onBlur={(e) => onPendingUserEmailBlur(null, idx, e.target.value, false)}
                      className="rounded-sm text-xs col-span-4"
                      data-testid={`pending-user-email-${idx}`} />
                    {pu.link_existing && pu.existing_user ? (
                      <div className="col-span-7 flex items-center gap-2 text-xs">
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-sm font-medium" data-testid={`pending-user-linked-${idx}`}>Lien : {pu.existing_user.name}</span>
                        {pu.existing_user.company_name && <span className="text-zinc-400">{pu.existing_user.company_name}</span>}
                      </div>
                    ) : (
                      <>
                        <Input placeholder="Nom *" value={pu.name} onChange={e => setFormNewUsers(prev => prev.map((u, i) => i === idx ? { ...u, name: e.target.value } : u))} className="rounded-sm text-xs col-span-3" />
                        <Input placeholder="Tél" value={pu.phone} onChange={e => setFormNewUsers(prev => prev.map((u, i) => i === idx ? { ...u, phone: e.target.value } : u))} className="rounded-sm text-xs col-span-2" />
                        <Input placeholder="Mot de passe" type="password" value={pu.password} onChange={e => setFormNewUsers(prev => prev.map((u, i) => i === idx ? { ...u, password: e.target.value } : u))} className="rounded-sm text-xs col-span-2" />
                      </>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setFormNewUsers(prev => prev.filter((_, i) => i !== idx))} className="h-7 w-7 p-0 text-red-400 col-span-1"><X size={12} /></Button>
                  </div>
                  {pu.existing_user && pu.link_existing && (
                    <div className="bg-blue-50 border border-blue-200 rounded-sm p-2 text-[11px] text-blue-800 flex items-center justify-between gap-2" data-testid={`pending-user-banner-${idx}`}>
                      <span>Cet email est déjà utilisé. Cet utilisateur sera <strong>lié</strong> à l'établissement (aucun nouveau compte créé).</span>
                      <Button variant="ghost" size="sm" onClick={() => setPendingUserLinkMode(null, idx, false, false)} className="h-6 text-[10px] px-2 text-blue-700">
                        Saisir un autre email
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {formNewUsers.length === 0 && <p className="text-xs text-zinc-400">Ajoutez des utilisateurs à cet établissement</p>}
            </div>

            <Button onClick={handleCreateSave} disabled={saving} className="w-full rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="save-create-btn">
              {saving ? 'Enregistrement...' : 'Créer le compte client'}
            </Button>
          </div>
        )}

        {/* VIEW / EDIT MODE */}
        {!createMode && companyFull && (
          <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-6" data-testid="company-detail">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading font-semibold text-lg">{companyFull.name}</h2>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <span className="text-xs font-mono bg-zinc-100 px-2 py-0.5 rounded">{companyFull.account_number}</span>
                  {companyFull.siret && <span className="text-xs text-zinc-400">SIRET: {companyFull.siret}</span>}
                  {companyFull.enseigne && <span className="text-xs text-zinc-500">Enseigne: {companyFull.enseigne}</span>}
                </div>
              </div>
              {!editMode && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)} className="rounded-sm"
                    title="Journal des modifications de ce compte" data-testid="open-history-btn">
                    <ClockCounterClockwise size={14} className="mr-1" />Historique
                  </Button>
                  {onCompanyMarquage && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onCompanyMarquage(companyFull)}
                      className={`rounded-sm ${companyFull.default_marquage_image_id ? 'text-indigo-700 border-indigo-300 bg-indigo-50' : ''}`}
                      title={companyFull.default_marquage_image_id ? 'Logo société actif (cliquer pour modifier)' : 'Attribuer un logo société'}
                      data-testid="company-marquage-btn"
                    >
                      <Stamp size={14} weight={companyFull.default_marquage_image_id ? 'fill' : 'regular'} className="mr-1" />
                      Marquage
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={startEdit} className="rounded-sm" data-testid="edit-company-btn">
                    <PencilSimple size={14} className="mr-1" />Modifier
                  </Button>
                </div>
              )}
              {editMode && <Button variant="ghost" size="sm" onClick={cancelEdit}><X size={18} /></Button>}
            </div>

            {editMode ? (
              <>
                <CompanyForm form={formCompany} setForm={setFormCompany} />
                <EstablishmentsForm
                  establishments={formEstablishments}
                  onAdd={addEstablishment} onRemove={removeEstablishment} onUpdate={updateEstablishment}
                  newUsers={editNewUsers}
                  onAddUser={(k) => setEditNewUsers(p => ({ ...p, [k]: [...(p[k] || []), { _id: Date.now(), name: '', email: '', phone: '', password: '', existing_user: null, link_existing: false }] }))}
                  onUpdateUser={(k, i, f, v) => setEditNewUsers(p => ({ ...p, [k]: (p[k] || []).map((u, idx) => idx === i ? { ...u, [f]: v, ...(f === 'email' ? { existing_user: null, link_existing: false } : {}) } : u) }))}
                  onUserEmailBlur={(k, i, v) => onPendingUserEmailBlur(k, i, v, true)}
                  onUserUnlink={(k, i) => setPendingUserLinkMode(k, i, false, true)}
                  onRemoveUser={(k, i) => setEditNewUsers(p => ({ ...p, [k]: (p[k] || []).filter((_, idx) => idx !== i) }))}
                  isCreate
                />
                <Button onClick={handleEditSave} disabled={saving} className="w-full rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="save-company-btn">
                  {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </Button>
              </>
            ) : (
              <>
                {(companyFull.email || companyFull.phone || companyFull.address) && (
                  <div className="flex gap-4 text-sm text-zinc-500 flex-wrap">
                    {companyFull.email && <span>{companyFull.email}</span>}
                    {companyFull.phone && <span>{companyFull.phone}</span>}
                    {companyFull.address && <span>{companyFull.address}</span>}
                  </div>
                )}
                <div className="space-y-4">
                  <h3 className="font-heading font-semibold text-sm text-zinc-700 uppercase tracking-wider">
                    <Buildings size={16} className="inline mr-2" />Établissements ({companyFull.establishments?.length || 0})
                  </h3>
                  {(companyFull.establishments || []).map(est => (
                    <EstablishmentCard key={est.id} est={est} onAddUser={() => setAddUserDialog({ open: true, estId: est.id })} onRefresh={() => loadCompanyFull(selectedCompanyId)} onDelete={handleDeleteEstablishment} onMerge={(e) => setMergeState({ open: true, source: e })} />
                  ))}
                  {(!companyFull.establishments || companyFull.establishments.length === 0) && (
                    <p className="text-sm text-zinc-400 italic py-2">Aucun établissement</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {!createMode && !companyFull && (
          <div className="bg-white border border-dashed border-zinc-300 rounded-sm p-16 flex flex-col items-center justify-center text-center">
            <Building2 size={48} className="text-zinc-300 mb-4" />
            <p className="text-zinc-500 text-sm">Sélectionnez un compte client ou créez-en un nouveau</p>
          </div>
        )}
      </div>

      {/* Corbeille / Fusion / Historique */}
      <TrashDialog open={trashOpen} onOpenChange={setTrashOpen}
        onRestored={async (est) => {
          await fetchCompanies();
          if (selectedCompanyId && est.company_id === selectedCompanyId) await loadCompanyFull(selectedCompanyId);
        }} />
      <MergeDialog open={mergeState.open} onOpenChange={(v) => setMergeState(p => ({ ...p, open: v }))}
        source={mergeState.source}
        candidates={companyFull?.establishments || []}
        onMerged={async () => { if (selectedCompanyId) await loadCompanyFull(selectedCompanyId); }} />
      <HistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} company={companyFull} />

      {/* Add User Dialog */}
      <Dialog open={addUserDialog.open} onOpenChange={(v) => { if (!v) { setAddUserDialog({ open: false, estId: '' }); setNewUserForm({ name: '', email: '', phone: '', password: '', existing_user: null }); } }}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Ajouter un utilisateur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-zinc-500">Email *</Label>
              <Input
                type="email"
                value={newUserForm.email}
                onChange={e => setNewUserForm(p => ({ ...p, email: e.target.value, existing_user: null }))}
                onBlur={handleDialogEmailBlur}
                className="rounded-sm mt-1"
                data-testid="add-user-email" />
            </div>
            {newUserForm.existing_user ? (
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 space-y-1" data-testid="add-user-existing-banner">
                <p className="text-xs font-semibold text-blue-900">Utilisateur existant détecté</p>
                <p className="text-sm text-blue-800">{newUserForm.existing_user.name} — {newUserForm.existing_user.email}</p>
                <p className="text-[11px] text-blue-700">Société : {newUserForm.existing_user.company_name || '—'} • {newUserForm.existing_user.establishment_ids?.length || 0} établissement(s) lié(s)</p>
                <p className="text-[11px] text-blue-700 italic">À la validation, cet utilisateur sera lié à l'établissement (aucun nouveau compte créé).</p>
                <Button variant="ghost" size="sm" onClick={() => setNewUserForm(p => ({ ...p, existing_user: null }))} className="text-[11px] h-7 px-2 mt-1" data-testid="add-user-existing-cancel">
                  Créer un autre compte avec un nouvel email
                </Button>
              </div>
            ) : (
              <>
                <div><Label className="text-xs text-zinc-500">Nom *</Label><Input value={newUserForm.name} onChange={e => setNewUserForm(p => ({ ...p, name: e.target.value }))} className="rounded-sm mt-1" data-testid="add-user-name" /></div>
                <div><Label className="text-xs text-zinc-500">Téléphone</Label><Input value={newUserForm.phone} onChange={e => setNewUserForm(p => ({ ...p, phone: e.target.value }))} className="rounded-sm mt-1" /></div>
                <div><Label className="text-xs text-zinc-500">Mot de passe</Label><Input type="password" value={newUserForm.password} onChange={e => setNewUserForm(p => ({ ...p, password: e.target.value }))} className="rounded-sm mt-1" /></div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddUserDialog({ open: false, estId: '' }); setNewUserForm({ name: '', email: '', phone: '', password: '', existing_user: null }); }} className="rounded-sm">Annuler</Button>
            <Button onClick={handleAddUserToEstablishment} className="rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="confirm-add-user">
              {newUserForm.existing_user ? 'Lier' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </div>
  );
}

// Bandeau d'alerte admin : établissements actifs sans adresse postale complète.
function MissingAddressAlert({ refreshKey, onSelect }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/establishments/missing-address`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [refreshKey]);
  if (!data || !data.count) return null;
  return (
    <div className="border border-amber-400 bg-amber-50 rounded-sm" data-testid="missing-address-alert">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left" data-testid="missing-address-toggle">
        <span className="flex items-center gap-2 text-sm font-medium text-amber-900">
          <Warning size={18} weight="fill" className="text-amber-600" />
          {data.count} établissement{data.count > 1 ? 's' : ''} sans adresse postale complète — les étiquettes de livraison seront incomplètes
        </span>
        {open ? <ChevronDown size={16} className="text-amber-700" /> : <ChevronRight size={16} className="text-amber-700" />}
      </button>
      {open && (
        <ul className="border-t border-amber-200 divide-y divide-amber-100 max-h-64 overflow-y-auto" data-testid="missing-address-list">
          {data.items.map(it => (
            <li key={it.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
              <div className="min-w-0">
                <span className="font-medium text-zinc-900">{it.name}</span>
                {it.company_name && it.company_name !== it.name && <span className="text-zinc-500"> — {it.company_name}</span>}
                <span className="ml-2 text-xs text-red-700">
                  {it.missing.includes('address') && 'Rue manquante'}{it.missing.length === 2 && ' · '}{it.missing.includes('ville') && 'Ville / CP manquants'}
                </span>
              </div>
              <Button size="sm" variant="outline" className="rounded-sm text-xs shrink-0" onClick={() => { setOpen(false); onSelect(it.company_id); }} data-testid={`missing-address-fix-${it.id}`}>
                <PencilSimple size={13} className="mr-1" />Compléter
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================
// SUB-TAB: Sociétés CRUD
// ============================================================
function SocietesTab() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', siret: '', address: '' });
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [trashOpen, setTrashOpen] = useState(false);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/companies`); setCompanies(r.data); }
    catch { toast.error('Erreur chargement sociétés'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const startCreate = () => {
    setCreating(true);
    setEditingId(null);
    setForm({ name: '', siret: '', address: '' });
  };

  const startEdit = (c) => {
    setCreating(false);
    setEditingId(c.id);
    setForm({ name: c.name || '', siret: c.siret || '', address: c.address || '' });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Le nom est obligatoire"); return; }
    if (!form.siret.trim()) { toast.error("Le SIRET est obligatoire"); return; }
    setSaving(true);
    try {
      if (creating) {
        await axios.post(`${API}/companies`, {
          name: form.name.trim(), siret: form.siret.trim(),
          email: '', phone: '', address: form.address?.trim() || '',
        });
        toast.success("Société créée");
      } else {
        await axios.put(`${API}/companies/${editingId}`, {
          name: form.name.trim(), siret: form.siret.trim(),
          address: form.address?.trim() || '',
        });
        toast.success("Société modifiée");
      }
      setCreating(false);
      setEditingId(null);
      await fetchCompanies();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, cascade = false) => {
    try {
      const r = await axios.delete(`${API}/companies/${id}${cascade ? '?cascade=true' : ''}`);
      const trashed = r.data?.establishments_trashed || 0;
      toast.success(trashed > 0
        ? `Société mise à la corbeille avec ${trashed} établissement(s) — récupérable 30 jours`
        : 'Société mise à la corbeille — récupérable 30 jours');
      setDeleteConfirm(null);
      await fetchCompanies();
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (!cascade && detail.includes('établissement')) {
        if (window.confirm(`${detail}\n\nSupprimer aussi ses établissements ?\n(Société et établissements seront récupérables pendant 30 jours via la corbeille. Les utilisateurs seront détachés, les commandes conservées.)`)) {
          return handleDelete(id, true);
        }
        setDeleteConfirm(null);
        return;
      }
      toast.error(detail || "Erreur suppression");
    }
  };

  const isEditing = creating || editingId;

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-semibold text-base">Sociétés</h2>
        {!isEditing && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setTrashOpen(true)} className="rounded-sm text-sm text-zinc-600" data-testid="societes-trash-btn">
              <Trash size={14} className="mr-2" />Corbeille
            </Button>
            <Button onClick={startCreate} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-sm" data-testid="create-societe-btn">
              <Plus size={14} className="mr-2" />Nouvelle société
            </Button>
          </div>
        )}
      </div>

      {/* Create / Edit form */}
      {isEditing && (
        <div className="bg-white border border-zinc-200 rounded-sm p-5 space-y-4" data-testid="societe-form">
          <h3 className="font-semibold text-sm">{creating ? 'Nouvelle société' : 'Modifier la société'}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">Nom de la société *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="rounded-sm mt-1 bg-white" data-testid="societe-name" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">SIRET *</Label>
              <Input value={form.siret} onChange={e => setForm(p => ({ ...p, siret: e.target.value }))} className="rounded-sm mt-1 bg-white" data-testid="societe-siret" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-zinc-500">Adresse du siège</Label>
              <Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} className="rounded-sm mt-1 bg-white" data-testid="societe-address" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="save-societe-btn">
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            <Button variant="outline" onClick={() => { setCreating(false); setEditingId(null); }} className="rounded-sm">Annuler</Button>
          </div>
        </div>
      )}

      {/* List */}
      {loading && <p className="text-sm text-zinc-400 text-center py-4">Chargement...</p>}
      <div className="space-y-2">
        {companies.map(c => (
          <div key={c.id} className="flex items-center justify-between p-3 bg-white border border-zinc-200 rounded-sm" data-testid={`societe-row-${c.id}`}>
            <div>
              <span className="font-medium text-sm">{c.name}</span>
              <span className={`text-[10px] font-medium ml-3 px-1.5 py-0.5 rounded-sm ${(c.est_count || 0) > 0 ? 'bg-zinc-100 text-zinc-600' : 'bg-zinc-50 text-zinc-400'}`} data-testid={`societe-est-count-${c.id}`}>
                {c.est_count || 0} établissement{(c.est_count || 0) > 1 ? 's' : ''}
              </span>
              {c.siret && <span className="text-xs text-zinc-400 ml-3">SIRET: {c.siret}</span>}
              {c.address && <span className="text-xs text-zinc-400 ml-3">{c.address}</span>}
            </div>
            <div className="flex gap-1.5">
              <Button variant="ghost" size="sm" onClick={() => startEdit(c)} className="h-7 w-7 p-0" data-testid={`edit-societe-${c.id}`}><PencilSimple size={14} /></Button>
              {deleteConfirm === c.id ? (
                <div className="flex gap-1">
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(c.id)} className="h-7 text-xs px-2" data-testid={`confirm-delete-societe-${c.id}`}>Confirmer</Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(null)} className="h-7 text-xs px-2" data-testid={`cancel-delete-societe-${c.id}`}>Annuler</Button>
                </div>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(c.id)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600" data-testid={`delete-societe-${c.id}`}><Trash size={14} /></Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <TrashDialog open={trashOpen} onOpenChange={setTrashOpen} onRestored={fetchCompanies} />
    </div>
  );
}

// ---- Sub-components ----

function CompanyForm({ form, setForm }) {
  return (
    <div className="space-y-3 bg-zinc-50 border border-zinc-200 p-4 rounded-sm">
      <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Informations société</p>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs text-zinc-500">Nom de la société *</Label>
          <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="rounded-sm mt-1 bg-white" data-testid="company-name-input" /></div>
        <div><Label className="text-xs text-zinc-500">SIRET</Label>
          <Input value={form.siret} onChange={e => setForm(p => ({ ...p, siret: e.target.value }))} className="rounded-sm mt-1 bg-white" data-testid="company-siret-input" /></div>
        <div><Label className="text-xs text-zinc-500">Nom de l'enseigne</Label>
          <Input value={form.enseigne || ''} onChange={e => setForm(p => ({ ...p, enseigne: e.target.value }))} className="rounded-sm mt-1 bg-white" /></div>
        <div><Label className="text-xs text-zinc-500">Email</Label>
          <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="rounded-sm mt-1 bg-white" /></div>
        <div><Label className="text-xs text-zinc-500">Téléphone</Label>
          <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="rounded-sm mt-1 bg-white" /></div>
        <div><Label className="text-xs text-zinc-500">Adresse du siège</Label>
          <Input value={form.address || ''} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} className="rounded-sm mt-1 bg-white" /></div>
      </div>
    </div>
  );
}

function EstablishmentsForm({ establishments, onAdd, onRemove, onUpdate, newUsers, onAddUser, onUpdateUser, onRemoveUser, onUserEmailBlur, onUserUnlink, isCreate }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wider"><Buildings size={14} className="inline mr-1" />Établissements</p>
        <Button variant="outline" size="sm" onClick={onAdd} className="rounded-sm text-xs" data-testid="add-establishment-btn">
          <Plus size={12} className="mr-1" />Ajouter
        </Button>
      </div>
      {establishments.map((est, idx) => {
        const estKey = est.id || est._tempId;
        const pendingUsers = newUsers?.[estKey] || [];
        return (
          <div key={estKey} className="border border-zinc-200 rounded-sm p-4 bg-white space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-zinc-600">Établissement {idx + 1}</span>
              <Button variant="ghost" size="sm" onClick={() => onRemove(idx)} title={est.id ? "Supprimer cet établissement" : "Retirer"} className="text-red-400 hover:text-red-600 h-6 w-6 p-0" data-testid={`remove-est-${idx}`}>
                {est.id ? <Trash size={14} /> : <X size={14} />}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Nom (enseigne) *" value={est.name} onChange={e => onUpdate(idx, 'name', e.target.value)} className="rounded-sm text-sm" data-testid={`est-name-${idx}`} />
              <Input placeholder="Ville" value={est.ville || ''} onChange={e => onUpdate(idx, 'ville', e.target.value)} className="rounded-sm text-sm" />
              <Input placeholder="Adresse" value={est.address} onChange={e => onUpdate(idx, 'address', e.target.value)} className="rounded-sm text-sm" />
              <Input placeholder="Email *" value={est.email} onChange={e => onUpdate(idx, 'email', e.target.value)} className="rounded-sm text-sm" />
              <Input placeholder="Téléphone" value={est.phone} onChange={e => onUpdate(idx, 'phone', e.target.value)} className="rounded-sm text-sm" />
            </div>
            {est.users && est.users.length > 0 && (
              <div className="ml-2 space-y-1">
                <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Utilisateurs rattachés</p>
                {est.users.map(u => (
                  <div key={u.id} className="flex items-center gap-2 text-xs text-zinc-600 bg-zinc-50 rounded-sm px-2 py-1.5">
                    <User size={12} className="text-zinc-400" />
                    <span className="font-medium">{u.name}</span>
                    <span className="text-zinc-400">{u.email}</span>
                  </div>
                ))}
              </div>
            )}
            {isCreate && pendingUsers.map((pu, puIdx) => (
              <div key={pu._id} className="ml-2 border border-zinc-200 rounded-sm p-2 bg-white space-y-2">
                <div className="grid grid-cols-12 gap-1.5 items-center">
                  <Input
                    placeholder="Email *"
                    value={pu.email}
                    onChange={e => onUpdateUser(estKey, puIdx, 'email', e.target.value)}
                    onBlur={e => onUserEmailBlur && onUserEmailBlur(estKey, puIdx, e.target.value)}
                    className="rounded-sm text-xs col-span-4" />
                  {pu.link_existing && pu.existing_user ? (
                    <div className="col-span-7 flex items-center gap-2 text-xs">
                      <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-sm font-medium">Lien : {pu.existing_user.name}</span>
                      {pu.existing_user.company_name && <span className="text-zinc-400">{pu.existing_user.company_name}</span>}
                    </div>
                  ) : (
                    <>
                      <Input placeholder="Nom *" value={pu.name} onChange={e => onUpdateUser(estKey, puIdx, 'name', e.target.value)} className="rounded-sm text-xs col-span-3" />
                      <Input placeholder="Tél" value={pu.phone} onChange={e => onUpdateUser(estKey, puIdx, 'phone', e.target.value)} className="rounded-sm text-xs col-span-2" />
                      <Input placeholder="Mot de passe" type="password" value={pu.password} onChange={e => onUpdateUser(estKey, puIdx, 'password', e.target.value)} className="rounded-sm text-xs col-span-2" />
                    </>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => onRemoveUser(estKey, puIdx)} className="h-7 w-7 p-0 text-red-400 col-span-1"><X size={12} /></Button>
                </div>
                {pu.existing_user && pu.link_existing && (
                  <div className="bg-blue-50 border border-blue-200 rounded-sm p-2 text-[11px] text-blue-800 flex items-center justify-between gap-2">
                    <span>Cet email est déjà utilisé. Cet utilisateur sera <strong>lié</strong> à l'établissement.</span>
                    {onUserUnlink && (
                      <Button variant="ghost" size="sm" onClick={() => onUserUnlink(estKey, puIdx)} className="h-6 text-[10px] px-2 text-blue-700">
                        Saisir un autre email
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {isCreate && (
              <Button variant="ghost" size="sm" onClick={() => onAddUser(estKey)} className="text-xs text-zinc-500 hover:text-zinc-700">
                <UserPlus size={12} className="mr-1" />Ajouter un utilisateur
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EstablishmentCard({ est, onAddUser, onRefresh, onDelete, onMerge }) {
  const [expanded, setExpanded] = useState(true);

  const togglePermission = async (userId, field, currentValue) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { [field]: !currentValue });
      toast.success(`Permission mise à jour`);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  return (
    <div className="border border-zinc-200 rounded-sm overflow-hidden" data-testid={`est-card-${est.id}`}>
      <div className="w-full flex items-center justify-between p-3 bg-zinc-50">
        <button onClick={() => setExpanded(!expanded)} className="flex-1 flex items-center gap-2 text-left hover:opacity-70 transition-opacity">
          {expanded ? <ChevronDown size={16} className="text-zinc-400" /> : <ChevronRight size={16} className="text-zinc-400" />}
          <MapPin size={14} className="text-zinc-500" />
          <span className="font-medium text-sm">{est.name}</span>
          {est.ville && <span className="text-xs text-zinc-400">({est.ville})</span>}
          {(!(est.address || '').trim() || !((est.ville || '').trim() || (est.code_postal || '').trim())) && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-sm" title="Adresse postale incomplète : les étiquettes de livraison seront incomplètes" data-testid={`est-missing-address-${est.id}`}>
              <Warning size={11} weight="bold" />Adresse manquante
            </span>
          )}
          <span className="text-xs text-zinc-400 ml-2">{est.users?.length || 0} utilisateur(s)</span>
        </button>
        {onMerge && (
          <Button variant="ghost" size="sm" onClick={() => onMerge(est)} title="Fusionner cet établissement dans un autre"
            className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100" data-testid={`merge-est-${est.id}`}>
            <GitMerge size={14} />
          </Button>
        )}
        {onDelete && (
          <Button variant="ghost" size="sm" onClick={() => onDelete(est)} title="Supprimer cet établissement"
            className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-est-${est.id}`}>
            <Trash size={14} />
          </Button>
        )}
      </div>
      {expanded && (
        <div className="p-3 space-y-2">
          {est.address && <p className="text-xs text-zinc-500"><MapPin size={12} className="inline mr-1" />{est.address}</p>}
          <div className="flex gap-4 text-xs text-zinc-400">
            {est.email && <span>{est.email}</span>}
            {est.phone && <span>{est.phone}</span>}
          </div>
          {(est.users || []).length > 0 && (
            <div className="mt-2 space-y-1">
              {est.users.map(u => (
                <div key={u.id} className="bg-white border border-zinc-100 rounded-sm px-3 py-2">
                  <div className="flex items-center gap-3">
                    <User size={14} className="text-zinc-400" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-zinc-800">{u.name}</span>
                      <span className="text-xs text-zinc-400 ml-2">{u.email}</span>
                      {u.is_active === false && <span className="text-[10px] ml-2 px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700">Désactivé</span>}
                    </div>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Retirer ${u.name} de ${est.name} ? Il ne pourra plus consulter ni passer de commandes pour cet établissement.`)) return;
                        try {
                          const r = await axios.delete(`${API}/establishments/${est.id}/users/${u.id}`);
                          toast.success(r.data?.account_deactivated
                            ? `${u.name} retiré — plus aucun établissement : compte désactivé`
                            : `${u.name} retiré de ${est.name}`);
                          onRefresh();
                        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors du retrait'); }
                      }}
                      title={`Retirer ${u.name} de cet établissement`}
                      className="h-6 w-6 flex items-center justify-center rounded-sm text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      data-testid={`detach-user-${u.id}-${est.id}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div className="flex items-center gap-3 mt-2 pl-7">
                    <button
                      onClick={() => togglePermission(u.id, 'can_view_prices', u.can_view_prices)}
                      className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-sm border transition-colors ${
                        u.can_view_prices
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          : 'bg-zinc-50 border-zinc-200 text-zinc-400 hover:border-zinc-300'
                      }`}
                      data-testid={`toggle-prices-${u.id}`}
                    >
                      {u.can_view_prices ? <Eye size={12} /> : <EyeSlash size={12} />}
                      Prix
                    </button>
                    <button
                      onClick={() => togglePermission(u.id, 'can_view_commercial_terms', u.can_view_commercial_terms)}
                      className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-sm border transition-colors ${
                        u.can_view_commercial_terms
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          : 'bg-zinc-50 border-zinc-200 text-zinc-400 hover:border-zinc-300'
                      }`}
                      data-testid={`toggle-terms-${u.id}`}
                    >
                      <Tag size={12} />
                      Conditions
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={onAddUser} className="text-xs text-zinc-500 hover:text-zinc-700 mt-1" data-testid={`add-user-to-est-${est.id}`}>
            <UserPlus size={12} className="mr-1" />Ajouter un utilisateur
          </Button>
        </div>
      )}
    </div>
  );
}
